import { FormLabel, Radiogroup, RadiogroupOptions, IValidateError, AppEmitter, APP_EMITTER_EVENTS } from "shared";
import { ITemplateRadioComponent, radioDirectionTypes } from "entities/template";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppLang } from "app";
import { useTranslation } from "react-i18next";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { transformMarkdown } from "../../../../lib/utils/markdown";
import styles from "./document_form_radio.module.scss";

interface IDocumentFormRadioProps {
    component: ITemplateRadioComponent;
    onChange: (tag: string, value?: string) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    externalUpdateRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    enLanguageEnabled: boolean;
    triggerValidate: number;
    labelShort: boolean;
}

export const DocumentFormRadio: React.FC<IDocumentFormRadioProps> = React.memo(({
                                                                                    component,
                                                                                    label,
                                                                                    labelShort,
                                                                                    tooltip,
                                                                                    readonly,
                                                                                    triggerValidate,
                                                                                    enLanguageEnabled,
                                                                                    externalUpdateRecord,
                                                                                    onValidationRegister,
                                                                                    onChange,
                                                                                }) => {
    const t = useComponentsTranslations();
    const [value, setValue] = useState<string | null>(null);
    const { i18n } = useTranslation();

    const required = useMemo(() => {
        return component.required !== false
    }, [component]);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        }
    }, [])

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true)
            }, component.name);
            onChange(component.name, undefined);
        }
    }, [])

    useEffect(() => {
        setTimeout(() => {
            const externalVal = externalUpdateRecord[component.name];
            if (!externalVal) {
                if (component.defaultValue) {
                    setValue(component.defaultValue);
                }
            }
        }, 100)
    }, [])

    useEffect(() => {
        if (component.name) {
            const radioValue = externalUpdateRecord[component.name];
            setValue(radioValue);
        }
    }, [externalUpdateRecord, component.name])

    useEffect(() => {
        if (component.name) {
            onValidationRegister(validateRadio, component.name);
            onChange(component.name, value ? value : undefined);
        }
    }, [component.name, value, required])

    const validateFunction = useCallback((value?: string | null): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            }
        }

        return {
            valid: true
        }
    }, [component, i18n.language]);

    const handleClearEvent = () => {
        if (!readonly) {
            setValue(null);
        }
    }

    const validateRadio = (): Promise<boolean> => {
        const valid = !required || validateFunction(value).valid;
        return Promise.resolve(valid);
    };

    const radioOptions: RadiogroupOptions[] = useMemo(() => {
        const ruList = component.list;
        const enList = enLanguageEnabled && component.list_en ? component.list_en : ruList;
        const list = i18n.language === AppLang.RU ? ruList : enList;
        if (!Array.isArray(ruList) || !Array.isArray(list)) return [];
        const options = ruList.map((item, index) => ({
            label: transformMarkdown(list[index] ? list[index] : item),
            value: ruList[index]
        }));

        return options;
    }, [i18n.language]);

    return component.name ? <FormLabel title={label} required={required} disabled={readonly} labelShort={labelShort}>
        <div className={styles.container}>
            <Radiogroup vertical={component.direction === radioDirectionTypes.col}
                        className={styles.input}
                        name="direction-radio"
                        options={radioOptions}
                        onChange={setValue}
                        value={value}
                        disabled={readonly}
                        validate={required}
                        validateFunc={validateFunction}
                        triggerValidate={triggerValidate}

            />
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
        </div>
    </FormLabel> : null;
});
